import { useEffect, useMemo } from "react";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { HubResourcesModule } from "./Components/HubResourcesModule/HubResourcesModule";
import { OtherSitesModule } from "./Components/OtherSitesModule/OtherSitesModule";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { WellKnownLearningMaterialCategories, WellKnownLearningMaterialSubCategories } from "UIPalette/LearningMaterials/wellKnownLearningMaterials";
import { CourseCard } from "../WisdomAcademy/Components/CourseCard/CourseCard";
import { useListCoursesFunctionRequest, useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { selectedInterestPrefix } from "core/helpers/preferencesFilter";
import useIsMobile from "shared/core/hooks/useIsMobile";
import { Breakpoint } from "shared/UI/ResponsiveBreakpoints";
import { WellKnownCourseCategories } from "../WisdomAcademy/wellKnownCourseCategories";
import { useLearningMaterialResourceModule } from "./hooks/useLearningMaterialResourceModule";

export const StudentHub = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile(Breakpoint.Desktop);
  const { trigger } = useLogReportingEventFunctionRequest();

  useEffect(() => {
    trigger({
      body: {
        reportingEventType: ReportingEventType.StudentHubOpened,
      },
    });
  }, [trigger]);
  
  const articles = useLearningMaterialResourceModule(
    WellKnownLearningMaterialCategories.articles,
    WellKnownLearningMaterialSubCategories.students,
    isMobile
  );

  const audioAndVideo = useLearningMaterialResourceModule(
    WellKnownLearningMaterialCategories.audioAndVideo,
    WellKnownLearningMaterialSubCategories.students,
    isMobile
  );

  const {
    data: coursesData,
    isLoading: isCoursesLoading,
    error: coursesError,
    mutate: mutateCourses,
  } = useListCoursesFunctionRequest({
    categories: [WellKnownCourseCategories.students],
    xClientCode: ClientInfoState.getCode(),
  });

  const courseCards = useMemo(
    () =>
      coursesData?.courses.map((course) => (
        <CourseCard key={course.id} courseData={course} className={classNames("min-h-full w-full", { "min-w-[300px]": isMobile })} />
      )),
    [coursesData, isMobile]
  );

  const resourceModules = [
    { heading: t("studentHub.resources.article"), ...articles },
    { heading: t("studentHub.resources.audioAndVideo"), ...audioAndVideo },
    {
      heading: t("studentHub.resources.wisdomAcademy"),
      cards: courseCards,
      isLoading: isCoursesLoading,
      error: coursesError,
      mutate: mutateCourses,
      onSeeAllClick: () => navigate(`/wisdom-academy#${selectedInterestPrefix}${WellKnownCourseCategories.students}`),
      className: "!h-[500px] !mt-6 lg:!mt-16",
    },
  ];

  const hasVisibleModuleBefore = (uptoIndex: number) => resourceModules.slice(0, uptoIndex).some((module) => !!module.cards?.length);

  return (
    <>
      <PageTitle title={t("pageTitle.studentHub")} />
      <PageHeader title={t("studentHub.heading")} hideForMobile />
      {resourceModules.map((resourceModule, index) => (
        <HubResourcesModule key={index} index={index} hasPreviousVisibleModule={hasVisibleModuleBefore(index)} {...resourceModule} />
      ))}
      <OtherSitesModule index={resourceModules.length} hasPreviousVisibleModule={hasVisibleModuleBefore(resourceModules.length)} />
    </>
  );
};
